import React, { useState } from 'react';
import { Eye, EyeOff, Copy, Check } from 'lucide-react';

interface MaskedMobileProps {
    mobile: string | number | null | undefined;
    // Country code without the leading "+" (e.g. "91", "1"). Defaults to "91" for call
    // sites that don't yet have this data on hand.
    countryCode?: string | number | null;
    // Click handler for the number text itself (e.g. navigate to the customer's profile).
    // Fires on both the masked and revealed states; reveal/copy buttons stop propagation.
    onClick?: (e: React.MouseEvent) => void;
    className?: string;
    // Shown in place of the number when there's nothing to display. Pass `null` to render nothing.
    fallback?: React.ReactNode;
    iconSize?: number;
}

// Masks a mobile number as "+91 ••••••1234", with an Eye/EyeOff toggle to reveal it and a
// Copy button — the same pattern already used for investor/referrer numbers in OrdersTab.tsx,
// pulled out here so every other list/card/detail view can show numbers the same way.
const MaskedMobile: React.FC<MaskedMobileProps> = ({ mobile, countryCode, onClick, className, fallback, iconSize = 11 }) => {
    const [revealed, setRevealed] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!mobile) {
        return fallback === null ? null : <>{fallback !== undefined ? fallback : <span className="text-[11px] text-slate-400">-</span>}</>;
    }

    const str = String(mobile);
    const code = String(countryCode || '91').replace('+', '');
    const masked = `+${code}\u00A0••••••${str.slice(-4)}`;
    const full = `+${code}\u00A0${str}`;

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(str);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        // A <span> root (not <div>) so this drops safely into call sites that nest the value
        // inside another inline element (e.g. an InfoItem's <span>text</span> wrapper).
        <span className="inline-flex items-center gap-1 whitespace-nowrap shrink-0">
            <span
                className={`${className ? className : 'text-[11px] text-slate-400'} whitespace-nowrap`}
                onClick={onClick}
            >
                {revealed ? full : masked}
            </span>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setRevealed(r => !r); }}
                className="p-0.5 rounded hover:bg-slate-100 border-none bg-transparent cursor-pointer shrink-0 inline-flex items-center justify-center"
                title={revealed ? 'Hide' : 'Show'}
            >
                {revealed ? <EyeOff size={iconSize} className="text-slate-400" /> : <Eye size={iconSize} className="text-slate-400" />}
            </button>
            <button
                type="button"
                onClick={handleCopy}
                className="p-0.5 rounded hover:bg-slate-100 border-none bg-transparent cursor-pointer shrink-0 inline-flex items-center justify-center"
                title="Copy mobile"
            >
                {copied ? <Check size={iconSize} className="text-green-500" /> : <Copy size={iconSize} className="text-slate-400" />}
            </button>
        </span>
    );
};

export default MaskedMobile;
